import React, { Component } from "react";
import Pagination from "../common/pagination";
import ClientTable from "./clientTable";
import ClientModal from "./Modals/Client/clientModal";
import AttestModal from "./Modals/Attest/attestModal";
import HistoryModal from "./Modals/History/historyModal";
import ClientRequestModal from "./Modals/Client/clientRequestModal";
import DropDown from "../common/dropDown";
import { exportAttestClients } from "../../services/complianceAPIServices";
import { processTableData, applyFilters } from "../common/Utils/tableUtils";
import { buildFiltersFromColumns } from "../common/Utils/tableUtils";
import { DEFAULT_STATUS_OPTIONS } from "./clientColumns";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Self-contained search input that manages its own typing state.
 * Keystrokes only re-render this small component, NOT the parent Clients tree.
 * The parent is only notified (via onSearch) after the debounce fires.
 */
class DebouncedSearchInput extends Component {
  constructor(props) {
    super(props);
    this.state = { value: props.value || "", isSearching: false };
    this._timer = null;
  }

  componentDidUpdate(prevProps) {
    // Sync if parent resets the value externally (e.g. clearAllFilters)
    if (prevProps.value !== this.props.value && this.props.value !== this.state.value) {
      this.setState({ value: this.props.value, isSearching: false });
      if (this._timer) clearTimeout(this._timer);
    }
  }

  componentWillUnmount() {
    if (this._timer) clearTimeout(this._timer);
  }

  handleChange = (e) => {
    const value = e.target.value;
    this.setState({ value, isSearching: value !== "" });

    if (this._timer) clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      this.setState({ isSearching: false });
      this.props.onSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  render() {
    const { placeholder } = this.props;
    const { value, isSearching } = this.state;
    return (
      <>
        <input
          type="text"
          className="form-control"
          aria-label="Text input with dropdown button"
          placeholder={placeholder}
          value={value}
          onChange={this.handleChange}
        />
        {isSearching && (
          <span className="input-group-text bg-transparent border-start-0">
            <div
              className="spinner-border spinner-border-sm text-secondary"
              role="status"
            >
              <span className="visually-hidden">Searching...</span>
            </div>
          </span>
        )}
      </>
    );
  }
}

// Module-level variable to persist FP selection across component remounts (SPA navigation)
// but NOT across full page refreshes — unlike sessionStorage which survives both.
let savedFPSelection = null;

export default class Clients extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pageSize: 6,
      currentPage: 1,
      searchPage: 1,
      sortColumn: {
        path: "client-name",
        order: "asc",
      },
      selectedType: 0,
      searchQuery: "",
      searchColumn: "Client Name(s)",
      columnsList: [],
      financial_professionals: [],
      selectedFP: [],
      selected: [],
      dateOutOfRange: false,
      selectedColumn: {
        path: "client-name",
        order: "asc",
      },
      clicked: { client: undefined, column: undefined },
      quickAttest: false,
      reviewNumber: 0,
      gracePeriod: false,
      // Multi-filter state - keys match column filterKey or path
      // Values are arrays for multi-select (empty array = no filter)
      filterState: {
        "financial-professional": [], // For admin/compliance users
        "attestation-status": [],
        "review-structure": [],
        "account-substantial-change": [],
        "account-r-t-a-changes": [],
        "reg-b-i-changes": [],
      },
      isExporting: false,
      // Track whether FP selection has changed (for admin refresh indicator)
      fpSelectionChanged: false,
      loadedFPs: [], // Track which FPs data has been loaded for
      fpInitialized: false, // Whether FP filter has been initialized (prevents re-init after user clears)
      isRefreshing: false, // Track when a multi-FP refresh is in progress
      externalOpenFilter: null, // Trigger to open a filter dropdown externally
      settingsOpen: false, // Settings gear dropdown visibility
      attestColumnFirst: localStorage.getItem("attestColumnFirst") === "true",
    };

    this.settingsRef = React.createRef();
  }

  // Memoization caches — skip expensive recomputation when only
  // searchInput / isSearching changed (i.e. mid-debounce keystrokes).
  _pagedDataCache = { inputs: {}, result: { totalCount: 0, data: [] } };
  _countsCache = {
    inputs: {},
    result: { total: 0, submitted: 0, partial: 0, notSubmitted: 0 },
  };

  _inputsChanged = (current, cached) => {
    const keys = Object.keys(current);
    if (keys.length !== Object.keys(cached).length) return true;
    return keys.some((k) => current[k] !== cached[k]);
  };

  componentDidMount = () => {
    this.handleGracePeriod();

    // Check if we have a saved selection from before remount (SPA navigation)
    if (savedFPSelection) {
      console.log(
        "componentDidMount: Restoring saved selection from module variable",
      );
      this.setState({
        filterState: {
          ...this.state.filterState,
          "financial-professional": savedFPSelection,
        },
        loadedFPs: savedFPSelection,
        fpInitialized: true,
      });
    } else {
      this.initializeFPState();
    }

    document.addEventListener("mousedown", this.handleSettingsClickOutside);
  };

  componentWillUnmount = () => {
    document.removeEventListener("mousedown", this.handleSettingsClickOutside);
  };

  /**
   * Initialize FP filter state based on currently loaded FP
   * This ensures the dropdown reflects what data is actually displayed
   */
  initializeFPState = () => {
    const { currentFP, groups, clients, financialProfessionals } = this.props;

    console.log("=== initializeFPState CALLED ===");
    console.log("currentFP:", currentFP);
    console.log("clients.length:", clients?.length);
    console.log(
      "financialProfessionals.length:",
      financialProfessionals?.length,
    );
    console.log("current loadedFPs:", this.state.loadedFPs);

    // Don't overwrite if user already has a selection
    if (this.state.loadedFPs.length > 0) {
      console.log(
        "initializeFPState: SKIPPING - already have selection, not overwriting",
      );
      return;
    }

    // Only for admin/compliance users who have FP selection
    if (groups.includes("compliance") || groups.includes("system_admin")) {
      if (currentFP && currentFP !== "" && currentFP !== "Multiple") {
        // Single FP loaded - use exact value from dropdown
        console.log("initializeFPState: Setting single FP:", currentFP);
        this.setState({
          filterState: {
            ...this.state.filterState,
            "financial-professional": [currentFP],
          },
          loadedFPs: [currentFP],
          fpInitialized: true,
        });
      } else if (
        clients &&
        clients.length > 0 &&
        financialProfessionals &&
        financialProfessionals.length > 0
      ) {
        // Multiple FPs loaded - find which dropdown options match loaded client data
        const matchedFPs = this.getMatchingDropdownOptions(
          clients,
          financialProfessionals,
        );
        console.log("initializeFPState: Setting matched FPs:", matchedFPs);
        if (matchedFPs.length > 0) {
          this.setState({
            filterState: {
              ...this.state.filterState,
              "financial-professional": matchedFPs,
            },
            loadedFPs: matchedFPs,
            fpInitialized: true,
          });
        }
      }
    }
  };

  /**
   * Get dropdown options that match loaded client data
   *
   * Dropdown values are group-ids (e.g. "Rosner Financial Group", "Michael C. Assad")
   * Matches against client group-id values (which may be semicolon-separated for multi-advisor)
   */
  getMatchingDropdownOptions = (clients, financialProfessionals) => {
    // Collect all unique group-id values from loaded clients
    const loadedGroupIds = new Set();
    clients.forEach((client) => {
      const groupId = client["group-id"];
      if (groupId) {
        // Handle multi-advisor clients (semicolon-separated)
        String(groupId)
          .split(";")
          .forEach((gid) => {
            const trimmed = gid.trim();
            if (trimmed) loadedGroupIds.add(trimmed);
          });
      }
    });

    // Match dropdown entries against loaded group-ids
    const matched = financialProfessionals.filter((fp) =>
      loadedGroupIds.has(fp),
    );

    console.log("Matched dropdown options:", matched);
    return matched;
  };

  componentDidUpdate = (prevProps) => {
    const { currentFP, groups, financialProfessionals, clients } = this.props;
    const { isRefreshing, loadedFPs, fpInitialized } = this.state;

    // When financialProfessionals becomes available and filter not yet initialized
    if (
      !isRefreshing &&
      !fpInitialized &&
      financialProfessionals &&
      financialProfessionals.length > 0 &&
      prevProps.financialProfessionals !== financialProfessionals &&
      clients &&
      clients.length > 0 &&
      (groups.includes("compliance") || groups.includes("system_admin"))
    ) {
      console.log("=== componentDidUpdate: AUTO-INITIALIZING FPs ===");
      console.log("loadedFPs was empty, initializing from client data");
      const matchedFPs = this.getMatchingDropdownOptions(
        clients,
        financialProfessionals,
      );
      console.log("componentDidUpdate: Setting matched FPs:", matchedFPs);
      if (matchedFPs.length > 0) {
        this.setState({
          filterState: {
            ...this.state.filterState,
            "financial-professional": matchedFPs,
          },
          loadedFPs: matchedFPs,
          fpInitialized: true,
        });
      }
    }

    // When currentFP changes from parent (single FP loaded externally)
    // Skip if a multi-FP refresh is in progress
    if (
      !isRefreshing &&
      prevProps.currentFP !== currentFP &&
      currentFP &&
      currentFP !== "" &&
      currentFP !== "Multiple" &&
      (groups.includes("compliance") || groups.includes("system_admin"))
    ) {
      if (!loadedFPs.includes(currentFP)) {
        console.log("=== componentDidUpdate: SINGLE FP CHANGE ===");
        console.log("Setting currentFP:", currentFP);
        this.setState({
          filterState: {
            ...this.state.filterState,
            "financial-professional": [currentFP],
          },
          loadedFPs: [currentFP],
          fpInitialized: true,
          fpSelectionChanged: false,
        });
      }
    }
  };

  handleGracePeriod = async () => {
    let currentMonth = new Date().getMonth();
    if (currentMonth <= 1) {
      this.setState({
        gracePeriod: true,
      });
    }
  };

  /**
   * Builds the shared filter array used by both getPagedData and getAttestationCounts.
   * Extracted to avoid duplicating filter-building logic.
   */
  buildFilters = () => {
    const { searchQuery, selectedColumn, filterState, columnsList } =
      this.state;

    // Build filters from column definitions and current filter state
    // Exclude "financial-professional" - we handle it separately with custom logic
    const fpExcludedFilterState = { ...filterState };
    delete fpExcludedFilterState["financial-professional"];
    const filters = buildFiltersFromColumns(columnsList, fpExcludedFilterState);

    // Closed filter is now handled at DynamoDB level in both Lambdas
    // system_admin: sees full DB including closed records
    // compliance/standard: closed records excluded via Attr('closed').not_exists()

    // Add Financial Professional filter (match dropdown value against client group-id)
    // Dropdown values are group-ids directly (e.g. "Rosner Financial Group")
    const fpFilter = filterState["financial-professional"] || [];
    if (fpFilter.length > 0) {
      filters.push({
        path: "group-id",
        value: fpFilter,
        type: "custom",
        customFn: (client, selectedFPs) => {
          // Match against both group-id and advisor-name
          // Admin dropdown uses group-ids, non-admin dropdown uses advisor-name parts
          const clientGroupIds = String(client["group-id"] || "")
            .split(";")
            .map((g) => g.trim())
            .filter((g) => g);
          const clientAdvisorNames = String(client["advisor-name"] || "")
            .split(/[/;]/)
            .map((a) => a.trim())
            .filter((a) => a);

          return selectedFPs.some((fp) => {
            const fpLower = fp.toLowerCase();
            return (
              clientGroupIds.some((g) => g.toLowerCase() === fpLower) ||
              clientAdvisorNames.some((a) => a.toLowerCase() === fpLower)
            );
          });
        },
      });
    }

    // Add search filter (contains match) if searching
    if (searchQuery) {
      filters.push({
        path: selectedColumn.path,
        value: searchQuery,
        type: "contains",
        allValue: "",
      });
    }

    return filters;
  };

  getPagedData = () => {
    const {
      pageSize,
      currentPage,
      sortColumn,
      searchQuery,
      searchPage,
      selectedColumn,
      columnsList,
      filterState,
    } = this.state;
    const { clients, groups } = this.props;

    // Return cached result when only unrelated state (searchInput, isSearching) changed
    const inputs = {
      pageSize, currentPage, sortColumn, searchQuery, searchPage,
      selectedColumn, columnsList, filterState, clients, groups,
    };
    if (!this._inputsChanged(inputs, this._pagedDataCache.inputs)) {
      return this._pagedDataCache.result;
    }

    if (clients === undefined) {
      const result = { totalCount: 0, data: [] };
      this._pagedDataCache = { inputs, result };
      return result;
    }

    // Compliance/admin users with no advisors selected should see empty view
    const isElevated =
      groups.includes("compliance") || groups.includes("system_admin");
    if (isElevated && (filterState["financial-professional"] || []).length === 0) {
      const result = { totalCount: 0, data: [] };
      this._pagedDataCache = { inputs, result };
      return result;
    }

    const filters = this.buildFilters();
    const activeSort = searchQuery ? selectedColumn : sortColumn;
    const columnConfig = activeSort
      ? columnsList.find((c) => (c.path || c.key) === activeSort.path) || null
      : null;

    // Use the standardized utility for filtering, sorting, and pagination
    const result = processTableData(clients, {
      filters,
      sortColumn: activeSort,
      columnConfig,
      currentPage: searchQuery === "" ? currentPage : searchPage,
      pageSize,
    });
    this._pagedDataCache = { inputs, result };
    return result;
  };

  handlePageChange = (page) => {
    if (this.state.searchQuery === "") {
      this.setState({ currentPage: page });
    } else {
      this.setState({ searchPage: page });
    }
    this.getPagedData();
    return false;
  };

  handleSort = (sortColumn) => {
    this.setState({ sortColumn });
  };

  updateColumnList = (newList) => {
    this.setState({ columnsList: newList });
  };

  handleFPChange = async (e) => {
    await this.props.getClients(e);
    let clients = this.props.clients;

    if (clients.length <= 0) {
      this.setState({ reviewNumber: 0 });
    }
  };

  objectsAreSame(x, y) {
    var objectsAreSame = true;
    for (var propertyName in x) {
      if (x[propertyName] !== y[propertyName]) {
        objectsAreSame = false;
        break;
      }
    }
    return objectsAreSame;
  }

  setClicked = (clicked) => this.setState({ clicked: clicked });

  eventForData = (clickedEvent) => {
    const condition = this.props.clients.find((c) =>
      this.objectsAreSame(c, clickedEvent),
    );
    if (condition) {
      return clickedEvent;
    }
    return {};
  };

  handlePageSize = (number) => {
    this.setState({ pageSize: number, currentPage: 1, searchPage: 1 });
  };

  toggleSettings = () => {
    this.setState((prev) => ({ settingsOpen: !prev.settingsOpen }));
  };

  handleSettingsClickOutside = (event) => {
    if (
      this.settingsRef.current &&
      !this.settingsRef.current.contains(event.target)
    ) {
      this.setState({ settingsOpen: false });
    }
  };

  handleAttestColumnToggle = () => {
    this.setState((prev) => {
      const newVal = !prev.attestColumnFirst;
      localStorage.setItem("attestColumnFirst", String(newVal));
      return { attestColumnFirst: newVal };
    });
  };

  /**
   * Handles filter change from FilterPanel
   * @param {string} filterKey - The key of the filter being changed
   * @param {string} value - The new filter value
   */
  handleFilterChange = (filterKey, value) => {
    console.log("=== HANDLE FILTER CHANGE ===");
    console.log("filterKey:", filterKey);
    console.log("value received:", value);
    console.log("value length:", value?.length);

    // Save FP selection to module variable to persist across remounts (but not page refresh)
    if (filterKey === "financial-professional") {
      savedFPSelection = value && value.length > 0 ? value : null;
    }

    this.setState((prevState) => {
      console.log(
        "prevState.filterState[filterKey]:",
        prevState.filterState[filterKey],
      );
      console.log("prevState.loadedFPs:", prevState.loadedFPs);

      const newFilterState = {
        ...prevState.filterState,
        [filterKey]: value,
      };

      // For FP filter: update loadedFPs to match user's selection
      // This prevents auto-initialization from overwriting manual selection
      let newLoadedFPs = prevState.loadedFPs;
      let fpSelectionChanged = prevState.fpSelectionChanged;

      if (filterKey === "financial-professional") {
        const selectedFPs = value || [];
        // Update loadedFPs to match user's manual selection
        newLoadedFPs = [...selectedFPs];
        // FP selection changed if user selected something different than what was loaded
        fpSelectionChanged = !this.arraysEqual(
          selectedFPs,
          prevState.loadedFPs,
        );
      }

      console.log(
        "Setting new filterState[filterKey]:",
        newFilterState[filterKey],
      );
      console.log("Setting new loadedFPs:", newLoadedFPs);

      return {
        filterState: newFilterState,
        loadedFPs: newLoadedFPs,
        currentPage: 1,
        searchPage: 1,
        fpSelectionChanged,
      };
    });
  };

  /**
   * Helper to compare two arrays for equality
   */
  arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  /**
   * Called when filter panel is closed - check if FP refresh is needed
   */
  handleFilterPanelClose = () => {
    const { filterState, loadedFPs } = this.state;
    const selectedFPs = filterState["financial-professional"] || [];

    // If FP selection differs from loaded data, keep the indicator showing
    if (!this.arraysEqual(selectedFPs, loadedFPs)) {
      this.setState({ fpSelectionChanged: true });
    }
  };

  /**
   * Handles FP data refresh - loads data for ALL selected FPs
   */
  handleFPRefresh = async () => {
    const { filterState } = this.state;
    const { currentFP, groups, getClientsForMultipleFPs, clearClients } =
      this.props;
    const selectedFPs = filterState["financial-professional"] || [];

    const isElevated =
      groups.includes("compliance") || groups.includes("system_admin");

    // If admin/compliance with no FP selection, clear loaded data
    if (isElevated && selectedFPs.length === 0) {
      if (clearClients) clearClients();
      this.setState({ fpSelectionChanged: false });
      return;
    }

    // If admin/compliance and has FP selection, load ALL selected FPs
    if (isElevated && selectedFPs.length > 0) {
      // Set refreshing flag and update loadedFPs BEFORE the async call
      // This prevents componentDidUpdate from resetting the filter state
      this.setState({
        isRefreshing: true,
        loadedFPs: [...selectedFPs],
      });

      try {
        const { financialProfessionals, getClients } = this.props;
        const allFPsSelected =
          selectedFPs.length === financialProfessionals.length;

        if (allFPsSelected && getClients) {
          // All FPs selected: use paginated full scan (single endpoint, guaranteed complete)
          await getClients("");
        } else if (getClientsForMultipleFPs) {
          // Subset of FPs: use per-FP GSI queries
          await getClientsForMultipleFPs(selectedFPs);
        } else {
          // Fallback to single FP if multi-FP function not available
          await this.handleFPChange(selectedFPs[0]);
        }
      } finally {
        // Clear the refreshing flag
        this.setState({
          isRefreshing: false,
          fpSelectionChanged: false,
        });
      }
    } else {
      // Standard refresh for non-admin users
      await this.handleFPChange(currentFP);
      this.setState({ fpSelectionChanged: false });
    }
  };

  clearAllFilters = () => {
    // Clear saved FP selection
    savedFPSelection = null;

    this.setState({
      filterState: {
        "financial-professional": [],
        "attestation-status": [],
        "review-structure": [],
        "account-substantial-change": [],
        "account-r-t-a-changes": [],
        "reg-b-i-changes": [],
      },
      loadedFPs: [],
      searchQuery: "",
      currentPage: 1,
      searchPage: 1,
    });
  };

  getActiveFilterCount = () => {
    const { filterState, searchQuery } = this.state;
    let count = 0;
    // Count active column filters (arrays with items = active filter)
    Object.entries(filterState).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) count++;
    });
    // Count search as a filter
    if (searchQuery) count++;
    return count;
  };

  handleExport = async (exportType, exportMode = "filtered") => {
    const { filterState, sortColumn } = this.state;

    this.setState({ isExporting: true });

    try {
      // For "tableview" and "detailed" exports, only apply advisor filter (no other column filters)
      // For "full" mode, no filters are applied
      // Only "both" with "filtered" mode applies all column filters
      const filterCriteria =
        exportMode === "filtered" && exportType === "both"
          ? {
              "attestation-status": filterState["attestation-status"] || [],
              "review-structure": filterState["review-structure"] || [],
              "account-substantial-change":
                filterState["account-substantial-change"] || [],
              "account-r-t-a-changes":
                filterState["account-r-t-a-changes"] || [],
              "reg-b-i-changes": filterState["reg-b-i-changes"] || [],
            }
          : {};

      // Get selected FPs (for admin/compliance users) - always sent for filtered mode
      const selectedFPs = filterState["financial-professional"] || [];

      const result = await exportAttestClients(
        exportMode,
        exportType,
        filterCriteria,
        selectedFPs,
        sortColumn,
      );

      if (result.success && result.downloadUrl) {
        // Trigger download
        const link = document.createElement("a");
        link.href = result.downloadUrl;
        link.download = result.fileName || "compliance_export.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert("Export failed: " + (result.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("Export failed. Please try again.");
    } finally {
      this.setState({ isExporting: false });
    }
  };

  getAttestationCounts = () => {
    const { clients, groups } = this.props;
    const { filterState, searchQuery, selectedColumn, columnsList } = this.state;

    // Return cached result when only unrelated state changed
    const inputs = { clients, groups, filterState, searchQuery, selectedColumn, columnsList };
    if (!this._inputsChanged(inputs, this._countsCache.inputs)) {
      return this._countsCache.result;
    }

    const zeroCounts = { total: 0, submitted: 0, partial: 0, notSubmitted: 0 };

    if (!clients || clients.length === 0) {
      this._countsCache = { inputs, result: zeroCounts };
      return zeroCounts;
    }

    // Compliance/admin users with no advisors selected should show zero counts
    const isElevated =
      groups.includes("compliance") || groups.includes("system_admin");
    if (isElevated && (filterState["financial-professional"] || []).length === 0) {
      this._countsCache = { inputs, result: zeroCounts };
      return zeroCounts;
    }

    // Reuse shared filter builder, apply filters directly (skip sort/pagination)
    const filters = this.buildFilters();
    const filtered = applyFilters(clients, filters);

    let submitted = 0;
    let partial = 0;
    let notSubmitted = 0;
    for (let i = 0; i < filtered.length; i++) {
      const status = filtered[i]["attestation-status"];
      if (status === "Submitted") submitted++;
      else if (status === "Partially Submitted") partial++;
      else if (status === "Not Submitted") notSubmitted++;
    }

    const result = { total: filtered.length, submitted, partial, notSubmitted };
    this._countsCache = { inputs, result };
    return result;
  };

  render() {
    const {
      groups,
      handleClientUpdate,
      handleAttestSubmit,
      serverRequests,
      financialProfessionals,
      submitClientUpdate,
      handleClientArchive,
      updatingFP,
      scrollHeight,
    } = this.props;
    const {
      pageSize,
      currentPage,
      searchPage,
      sortColumn,
      selectAll,
      selected,
      clicked,
      quickAttest,
      columnsList,
      searchColumn,
      gracePeriod,
      filterState,
      isExporting,
      fpSelectionChanged,
      externalOpenFilter,
    } = this.state;
    const activeFilterCount = this.getActiveFilterCount();
    const attestationCounts = this.getAttestationCounts();
    const { totalCount, data: clients } = this.getPagedData();
    const submittedPercentage =
      attestationCounts.total > 0
        ? Math.round(
            (attestationCounts.submitted / attestationCounts.total) * 100,
          )
        : 0;

    return (
      <div onScroll={this.handleScroll}>
        <div className="page-margin-top">
          {clicked &&
            clicked.column === "client" &&
            this.eventForData(clicked.data) && (
              <div style={{ height: "0" }}>
                <ClientModal
                  scrollHeight={scrollHeight}
                  GetScrollPosition={this.props.GetScrollPosition}
                  clients={this.props.clients}
                  data={this.eventForData(clicked.data)}
                  onClose={() => this.setState({ clicked: null })}
                  accountName={this.eventForData(clicked.data)["client-name"]}
                  groupId={this.eventForData(clicked.data)["group-id"]}
                  groups={groups}
                  handleClientUpdate={handleClientUpdate}
                  submitClientUpdate={submitClientUpdate}
                  handleClientArchive={handleClientArchive}
                />
              </div>
            )}
          {clicked && clicked.column === "attest" && (
            <div style={{ height: "0" }}>
              <AttestModal
                GetScrollPosition={this.props.GetScrollPosition}
                scrollHeight={scrollHeight}
                quickAttest={quickAttest}
                data={this.eventForData(clicked.data)}
                onClose={() =>
                  this.setState({ clicked: null, quickAttest: false })
                }
                onSubmit={handleAttestSubmit}
                accountName={
                  this.eventForData(clicked.data)
                    ? this.eventForData(clicked.data)["client-name"]
                    : ""
                }
                groupId={this.eventForData(clicked.data)["group-id"]}
                groups={groups}
                clients={this.props.clients}
                handleClientUpdate={handleClientUpdate}
                gracePeriod={gracePeriod}
              />
            </div>
          )}
          {clicked && clicked.column === "history" && (
            <div style={{ height: "0" }}>
              <HistoryModal
                scrollHeight={scrollHeight}
                GetScrollPosition={this.props.GetScrollPosition}
                quickAttest={quickAttest}
                data={this.eventForData(clicked.data)}
                onClose={() =>
                  this.setState({ clicked: null, quickAttest: false })
                }
                accountName={this.eventForData(clicked.data)["client-name"]}
                groupId={this.eventForData(clicked.data)["group-id"]}
              />
            </div>
          )}
          {clicked && clicked.column === "request" && (
            <div style={{ height: "0" }}>
              <ClientRequestModal
                scrollHeight={scrollHeight}
                GetScrollPosition={this.props.GetScrollPosition}
                data={this.eventForData(clicked.data)}
                onClose={() => this.setState({ clicked: null })}
              />
            </div>
          )}
          <div className="container-xxl">
              <div className="card">
                <div className="card-body row">
                  {/* Attestation Summary Header - 2 column layout (2:1 ratio) */}
                  <div className="col-12 mb-3">
                    <div className="d-flex align-items-center gap-3">
                      {/* Left section: Title + Progress Bar + Legend (flex: 2) */}
                      <div
                        style={{ flex: 2, minWidth: "200px" }}
                        className="text-start"
                      >
                        <strong className="d-block mb-1">
                          Table Summary: {attestationCounts.submitted} of{" "}
                          {attestationCounts.total} submitted (
                          {submittedPercentage}%)
                        </strong>
                        <div className="progress" style={{ height: "15px" }}>
                          {attestationCounts.total > 0 && (
                            <>
                              <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{
                                  width: `${
                                    (attestationCounts.submitted /
                                      attestationCounts.total) *
                                    100
                                  }%`,
                                }}
                                aria-valuenow={attestationCounts.submitted}
                                aria-valuemin="0"
                                aria-valuemax={attestationCounts.total}
                              >
                                {attestationCounts.submitted > 0 &&
                                  attestationCounts.submitted}
                              </div>
                              <div
                                className="progress-bar bg-warning"
                                role="progressbar"
                                style={{
                                  width: `${
                                    (attestationCounts.partial /
                                      attestationCounts.total) *
                                    100
                                  }%`,
                                }}
                                aria-valuenow={attestationCounts.partial}
                                aria-valuemin="0"
                                aria-valuemax={attestationCounts.total}
                              >
                                {attestationCounts.partial > 0 &&
                                  attestationCounts.partial}
                              </div>
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                  backgroundColor: "#385EA4",
                                  width: `${
                                    (attestationCounts.notSubmitted /
                                      attestationCounts.total) *
                                    100
                                  }%`,
                                }}
                                aria-valuenow={attestationCounts.notSubmitted}
                                aria-valuemin="0"
                                aria-valuemax={attestationCounts.total}
                              >
                                {attestationCounts.notSubmitted > 0 &&
                                  attestationCounts.notSubmitted}
                              </div>
                            </>
                          )}
                        </div>
                        {/* Legend */}
                        <div className="d-flex gap-3 small mt-1">
                          <span>
                            <span
                              className="badge bg-success me-1"
                              style={{ width: "12px", height: "12px" }}
                            >
                              &nbsp;
                            </span>
                            Submitted: {attestationCounts.submitted}
                          </span>
                          <span>
                            <span
                              className="badge bg-warning me-1"
                              style={{ width: "12px", height: "12px" }}
                            >
                              &nbsp;
                            </span>
                            Partial: {attestationCounts.partial}
                          </span>
                          <span>
                            <span
                              className="badge me-1"
                              style={{ width: "12px", height: "12px", backgroundColor: "#385EA4" }}
                            >
                              &nbsp;
                            </span>
                            Remaining: {attestationCounts.notSubmitted}
                          </span>
                        </div>
                      </div>

                      {/* Right section: Buttons (flex: 1) */}
                      <div
                        style={{ flex: 1 }}
                        className="d-flex gap-2 align-items-center justify-content-end"
                      >
                        {/* FP Loading Indicator */}
                        {(groups.includes("compliance") ||
                          groups.includes("system_admin")) &&
                          serverRequests["FPs"] && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          )}
                        {/* Export Button */}
                        <div className="btn-group">
                          <button
                            type="button"
                            className="btn btn-success"
                            onClick={() =>
                              this.handleExport("tableview", "filtered")
                            }
                            disabled={isExporting}
                          >
                            {isExporting && (
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                              ></span>
                            )}
                            Export to Excel
                          </button>
                          <button
                            type="button"
                            className="btn btn-success dropdown-toggle dropdown-toggle-split"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            disabled={isExporting}
                          >
                            <span className="visually-hidden">
                              Toggle Dropdown
                            </span>
                          </button>
                          <ul className="dropdown-menu">
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={(e) => {
                                  e.target.closest(".btn-group").querySelector("[data-bs-toggle='dropdown']").click();
                                  this.handleExport("account", "filtered");
                                }}
                              >
                                Export Accounts
                              </button>
                            </li>
                            {(groups.includes("compliance") ||
                              groups.includes("system_admin")) && (
                              <>
                                <li>
                                  <hr className="dropdown-divider" />
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={(e) => {
                                      e.target.closest(".btn-group").querySelector("[data-bs-toggle='dropdown']").click();
                                      this.handleExport("tableview", "full");
                                    }}
                                  >
                                    Export Full Database
                                  </button>
                                </li>
                              </>
                            )}
                          </ul>
                        </div>
                        {/* Refresh Button */}
                        <button
                          style={{
                            backgroundColor: "#385EA4",
                            color: "white",
                            animation: fpSelectionChanged
                              ? "pulse 1.5s ease-in-out infinite"
                              : "none",
                          }}
                          className={`btn ${
                            fpSelectionChanged ? "btn-pulse" : ""
                          }`}
                          onClick={async () => this.handleFPRefresh()}
                          disabled={updatingFP}
                          title={
                            fpSelectionChanged
                              ? "Financial Professional selection changed - click to refresh data"
                              : "Refresh client data"
                          }
                        >
                          {updatingFP && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden"></span>
                            </div>
                          )}{" "}
                          {fpSelectionChanged ? "Refresh Data" : "Refresh"}
                          {fpSelectionChanged && !updatingFP && (
                            <span
                              className="badge bg-warning text-dark ms-1"
                              style={{ fontSize: "0.65rem" }}
                            >
                              !
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mt-2">
                    {/* FP Selection Changed Warning */}
                    {fpSelectionChanged && (
                      <div
                        className="alert alert-warning py-2 mb-3"
                        role="alert"
                      >
                        <strong>Advisor selection changed</strong> - Click the
                        Refresh button above to load data for selected advisors.
                      </div>
                    )}
                    {/* Search Row */}
                    <div className="input-group mb-3">
                      <DropDown
                        title={"Search"}
                        items={columnsList.map((c) => (c = c.label))}
                        textProperty={""}
                        valueProperty={""}
                        selectedItem={searchColumn}
                        onItemSelect={(e) => {
                          this.setState({
                            selectedColumn: {
                              path: columnsList.find((cl) => cl.label === e)
                                .path
                                ? columnsList.find((cl) => cl.label === e).path
                                : columnsList.find((cl) => cl.label === e).key,
                              order: "asc",
                            },
                            sortColumn: {
                              path: columnsList.find((cl) => cl.label === e)
                                .path
                                ? columnsList.find((cl) => cl.label === e).path
                                : columnsList.find((cl) => cl.label === e).key,
                              order: "asc",
                            },
                            searchColumn: e,
                          });
                        }}
                      />
                      <DebouncedSearchInput
                        placeholder={searchColumn}
                        value={this.state.searchQuery}
                        onSearch={(value) =>
                          this.setState({ searchQuery: value, searchPage: 1 })
                        }
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <DropDown
                        title={"Clients Per Page"}
                        items={[6, 10, 20, 50, 100, 1000]}
                        textProperty={""}
                        valueProperty={""}
                        selectedItem={pageSize}
                        onItemSelect={this.handlePageSize}
                      />
                      <div
                        className="dropdown"
                        ref={this.settingsRef}
                        style={{ position: "relative" }}
                      >
                        <button
                          className="btn btn-link d-flex align-items-center justify-content-center p-1"
                          onClick={this.toggleSettings}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="#385EA4"
                            viewBox="0 0 16 16"
                          >
                            <path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z" />
                          </svg>
                        </button>
                        {this.state.settingsOpen && (
                          <div
                            className="dropdown-menu show"
                            style={{
                              position: "absolute",
                              minWidth: "230px",
                              right: 0,
                            }}
                          >
                            <div
                              className="dropdown-item d-flex align-items-center justify-content-between"
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                this.handleAttestColumnToggle();
                              }}
                            >
                              <span>Attest Column First</span>
                              <div className="form-check form-switch mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={this.state.attestColumnFirst}
                                  onChange={() => {}}
                                  style={{ pointerEvents: "none" }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mb-3"></div>
                  {/* Select Advisors prompt when no advisors selected (admin/compliance only) */}
                  {(groups.includes("compliance") ||
                    groups.includes("system_admin")) &&
                    filterState["financial-professional"].length === 0 &&
                      totalCount === 0 &&
                      !this.state.searchQuery &&
                      activeFilterCount === 0 &&
                    financialProfessionals &&
                    financialProfessionals.length > 0 && (
                      <div
                        className="alert alert-info py-2 mb-3 d-flex align-items-center justify-content-between"
                        role="alert"
                      >
                        <span>
                          <strong>No advisors selected.</strong> Select
                          advisors to view their clients.
                        </span>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ backgroundColor: "#385EA4", borderColor: "#385EA4" }}
                          onClick={(e) => {
                            e.nativeEvent.stopImmediatePropagation();
                            this.setState({
                              externalOpenFilter: "financial-professional",
                            });
                          }}
                        >
                          Select Advisors
                        </button>
                      </div>
                    )}
                  <div style={{ marginBottom: "20px" }}>
                    <ClientTable
                      updateColumnList={this.updateColumnList}
                      showDetails={true}
                      clients={clients}
                      allClients={this.props.clients} // Full dataset for filter options
                      sortColumn={sortColumn}
                      onSort={this.handleSort}
                      selected={selected}
                      selectAll={selectAll}
                      handleToggleRow={this.handleToggleRow}
                      handleToggleSelectAll={this.handleToggleSelectAll}
                      setClicked={this.setClicked}
                      groups={groups}
                      gracePeriod={gracePeriod}
                      // Filter props for per-column filters
                      filterState={filterState}
                      onFilterChange={this.handleFilterChange}
                      defaultFilterOptions={{
                        "attestation-status": DEFAULT_STATUS_OPTIONS,
                      }}
                      financialProfessionals={financialProfessionals}
                      externalOpenFilter={externalOpenFilter}
                      onExternalFilterOpened={() =>
                        this.setState({ externalOpenFilter: null })
                      }
                      attestColumnFirst={this.state.attestColumnFirst}
                    />
                    {totalCount === 0 &&
                      !serverRequests["clients"] &&
                      !serverRequests["FPs"] && (
                        <div className="text-center text-muted py-4">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="48"
                            height="48"
                            fill="currentColor"
                            className="mb-2 opacity-50"
                            viewBox="0 0 16 16"
                          >
                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
                          </svg>
                          <p className="mb-1">
                            <strong>No results found</strong>
                          </p>
                          {activeFilterCount > 0 ? (
                            <>
                              <p className="mb-2 small">
                                Try adjusting your filters or search text.
                              </p>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={this.clearAllFilters}
                              >
                                Clear All Filters
                              </button>
                            </>
                          ) : (
                            <p className="mb-0 small">
                              No client data is currently loaded.
                            </p>
                          )}
                        </div>
                      )}
                    <Pagination
                      itemsCount={totalCount}
                      pageSize={pageSize}
                      currentPage={
                        this.state.searchQuery === "" ? currentPage : searchPage
                      }
                      onPageChange={this.handlePageChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    );
  }
}
